import { useEffect, useState } from "react";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { LogicLineMark, Spinner } from "@/components/ui/Logo";

/** Where "IT kontaktieren" actually goes. Someone who cannot log in cannot reach any
 * in-app support surface, so this has to be plain mail. */
const IT_CONTACT_EMAIL = "it@logicline.eu";
import { useAuth } from "@/hooks/useAuth";
import { BRAND } from "@/lib/tokens";
import { t } from "@/lib/strings";

export function LoginPage() {
  const { isAuthenticated, login } = useAuth();
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as { returnTo?: string };
  const returnTo = search.returnTo;
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) return;
    // SPA navigation — a full window.location reload would reset the
    // module-level authStore back to its localStorage-seeded value and any
    // race against beforeLoad re-triggers /login → loop.
    const target = returnTo && returnTo.startsWith("/") ? returnTo : "/";
    void navigate({ to: target, replace: true });
  }, [isAuthenticated, navigate, returnTo]);

  const onClick = async () => {
    setLoading(true);
    try {
      await login(returnTo);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        width: "100%",
        display: "flex",
        alignItems: "stretch",
        justifyContent: "stretch",
        fontFamily: "Inter Variable, Inter, system-ui, sans-serif",
      }}
    >
      <div
        style={{
          flex: 1.1,
          minWidth: 0,
          background: "linear-gradient(155deg, #00599B 0%, #074a82 60%, #06223A 100%)",
          color: "#fff",
          padding: "48px 56px",
          position: "relative",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          overflow: "hidden",
        }}
      >
        <div
          aria-hidden
          style={{
            position: "absolute",
            right: -80,
            bottom: -80,
            width: 480,
            height: 480,
            opacity: 0.1,
            background: `repeating-linear-gradient(-18deg, ${BRAND.orange} 0 12px, transparent 12px 28px)`,
          }}
        ></div>
        <div
          aria-hidden
          style={{
            position: "absolute",
            left: -120,
            top: -60,
            width: 360,
            height: 360,
            opacity: 0.06,
            background: `repeating-linear-gradient(-18deg, #fff 0 8px, transparent 8px 20px)`,
          }}
        ></div>

        <div style={{ position: "relative", zIndex: 1 }}>
          <LogicLineMark height={26} />
        </div>

        <div style={{ position: "relative", zIndex: 1 }}>
          <h1
            style={{
              margin: 0,
              fontSize: 48,
              fontWeight: 600,
              lineHeight: 1.05,
              letterSpacing: "-0.02em",
            }}
          >
            Flow.
          </h1>
        </div>

        <div
          style={{ position: "relative", zIndex: 1, fontSize: 12, color: "rgba(255,255,255,0.55)" }}
        >
          {t.hostedOn} <span style={{ color: BRAND.orange }}>flow.logicline.dev</span>
        </div>
      </div>

      <div
        style={{
          flex: 1,
          background: "var(--surface)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 40,
        }}
      >
        <div style={{ width: 360, maxWidth: "100%" }}>
          <div
            style={{
              fontSize: 11,
              fontWeight: 600,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              color: "var(--ink-3)",
            }}
          >
            {t.welcomeBack}
          </div>
          <h2
            style={{
              margin: "8px 0 12px",
              fontSize: 28,
              fontWeight: 600,
              color: "var(--ink-1)",
              letterSpacing: "-0.01em",
            }}
          >
            {t.signInToFlow}
          </h2>
          <p style={{ margin: "0 0 28px", fontSize: 14, lineHeight: 1.55, color: "var(--ink-3)" }}>
            {t.ssoBlurb}
          </p>

          <button
            onClick={() => void onClick()}
            disabled={loading}
            style={{
              width: "100%",
              height: 46,
              borderRadius: 10,
              border: "none",
              background: BRAND.blue,
              color: "#fff",
              fontSize: 15,
              fontWeight: 500,
              cursor: loading ? "wait" : "pointer",
              fontFamily: "Inter Variable, Inter, system-ui, sans-serif",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 10,
              boxShadow: "0 1px 0 rgba(0,0,0,0.04), 0 4px 16px rgba(0,89,155,0.18)",
              opacity: loading ? 0.85 : 1,
            }}
          >
            {loading ? (
              <>
                <Spinner color="#fff" /> {t.signingIn}
              </>
            ) : (
              <>
                <span
                  style={{
                    width: 16,
                    height: 16,
                    borderRadius: 3,
                    background: BRAND.orange,
                    transform: "skewX(-18deg)",
                    display: "inline-block",
                  }}
                />
                {t.signIn}
              </>
            )}
          </button>

          {/* Flow is SSO-only. The "Mit E-Mail-Adresse fortfahren" button that used to sit
              here — with its "oder" divider — was a DEAD control: it had no onClick at all,
              so it never did anything. It promised a second way in that does not exist. */}

          <p style={{ margin: "32px 0 0", fontSize: 11.5, color: "var(--ink-3)", lineHeight: 1.6 }}>
            {t.termsBlurb} {t.problemsLogin}{" "}
            {/* Was href="#" — a DEAD link, styled and coloured like a real one, offered to
                someone who by definition cannot get in. The third dead control found on this
                page alone (after the "Mit E-Mail-Adresse fortfahren" button and the inert
                Teilnehmer chip). A subject line so IT can triage it without asking. */}
            <a
              href={`mailto:${IT_CONTACT_EMAIL}?subject=${encodeURIComponent("Flow: Probleme bei der Anmeldung")}`}
              style={{ color: "var(--accent)", textDecoration: "none" }}
            >
              {t.contactIt}
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
